import React, { useState } from "react";
import { X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AUTH_MODAL, LOGIN, REGISTER } from "@/constants/testIds";
import { toast } from "sonner";

const AuthModal = () => {
  const { authModalOpen, closeAuthModal, login, register, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (!authModalOpen) return null;

  const reset = () => {
    setForm({ name: "", email: "", password: "", confirm: "" });
    setError("");
  };

  const close = () => {
    reset();
    closeAuthModal();
  };

  const field = (k) => ({
    value: form[k],
    onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })),
  });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back to the procession.");
      } else {
        if (form.password !== form.confirm) {
          setError("Passwords do not match.");
          setBusy(false);
          return;
        }
        if (form.password.length < 6) {
          setError("Password must be at least 6 characters.");
          setBusy(false);
          return;
        }
        await register({ name: form.name, email: form.email, password: form.password });
        toast.success("Your walk begins. Account created.");
      }
      close();
    } catch (err) {
      setError(err?.response?.data?.detail || "Something broke. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full mt-2 bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-foreground";
  const labelCls = "text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground";

  return (
    <div
      data-testid={AUTH_MODAL.root}
      className="fixed inset-0 z-[10000] flex items-center justify-center p-5"
    >
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-sm"
        onClick={close}
      />
      <div className="relative w-full max-w-md bg-card border border-border p-8 md:p-10 fade-up">
        <button
          data-testid={AUTH_MODAL.close}
          onClick={close}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="font-serif text-4xl uppercase tracking-tighter leading-none">
          {mode === "login" ? "Enter" : "Join"}
        </h2>
        <p className="mt-3 mb-8 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
          {mode === "login" ? "Return to the walk" : "Begin the procession"}
        </p>

        <button
          data-testid={AUTH_MODAL.googleButton}
          onClick={loginWithGoogle}
          className="w-full flex items-center justify-center gap-3 border border-border py-3 font-mono text-xs uppercase tracking-[0.2em] hover:border-foreground transition-colors mb-6"
        >
          <img
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
            alt=""
            className="w-4 h-4"
          />
          Continue with Google
        </button>

        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-border" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            or
          </span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <form onSubmit={submit} className="space-y-5">
          {mode === "register" && (
            <div>
              <label className={labelCls}>Name</label>
              <input data-testid={REGISTER.nameInput} className={inputCls} {...field("name")} />
            </div>
          )}
          <div>
            <label className={labelCls}>Email</label>
            <input
              required
              type="email"
              data-testid={mode === "login" ? LOGIN.emailInput : REGISTER.emailInput}
              className={inputCls}
              {...field("email")}
            />
          </div>
          <div>
            <label className={labelCls}>Password</label>
            <input
              required
              type="password"
              data-testid={mode === "login" ? LOGIN.passwordInput : REGISTER.passwordInput}
              className={inputCls}
              {...field("password")}
            />
          </div>
          {mode === "register" && (
            <div>
              <label className={labelCls}>Confirm Password</label>
              <input
                required
                type="password"
                data-testid={REGISTER.passwordConfirmInput}
                className={inputCls}
                {...field("confirm")}
              />
            </div>
          )}

          {error && (
            <p data-testid={AUTH_MODAL.error} className="font-mono text-xs text-destructive">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid={mode === "login" ? LOGIN.submitButton : REGISTER.submitButton}
            className="w-full bg-foreground text-background py-4 text-xs font-mono uppercase tracking-[0.3em] hover:bg-[#5B2A86] hover:text-foreground transition-colors disabled:opacity-50"
          >
            {busy ? "…" : mode === "login" ? "Enter" : "Create Account"}
          </button>
        </form>

        <button
          data-testid={AUTH_MODAL.toggleMode}
          onClick={() => {
            setMode((m) => (m === "login" ? "register" : "login"));
            setError("");
          }}
          className="mt-6 w-full text-center font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors"
        >
          {mode === "login" ? "No account? Join the procession" : "Already walking? Enter"}
        </button>
      </div>
    </div>
  );
};

export default AuthModal;
